import React, { useState } from 'react';
import { authAPI } from '@/services/api';
import { Lock, Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * Re-usable Change-Password card. Drops in to any role's Settings page.
 * Props:
 *   testIdPrefix : optional string prefix for data-testid attrs (default: 'change-pw')
 */
export default function ChangePasswordCard({ testIdPrefix = 'change-pw' }) {
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState('');
  const [isErr, setIsErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    setIsErr(false);
    if (newPw.length < 6) { setMsg('New password must be at least 6 characters'); setIsErr(true); return; }
    if (newPw !== confirmPw) { setMsg('Passwords do not match'); setIsErr(true); return; }
    if (curPw === newPw) { setMsg('New password must differ from current'); setIsErr(true); return; }
    setLoading(true);
    try {
      await authAPI.changePassword(curPw, newPw);
      setMsg('Password changed successfully.');
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setMsg('Error: ' + (err?.response?.data?.detail || err.message || 'Failed'));
      setIsErr(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid={`${testIdPrefix}-card`}>
      <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-4">
        <Lock className="w-4 h-4" /> Change Password
      </h3>
      {msg && (
        <p className={`text-sm mb-3 ${isErr ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`} data-testid={`${testIdPrefix}-msg`}>
          {msg}
        </p>
      )}
      <form onSubmit={submit} className="space-y-3">
        <div className="relative">
          <input
            type={showCur ? 'text' : 'password'}
            value={curPw}
            onChange={e => setCurPw(e.target.value)}
            placeholder="Current password"
            required
            data-testid={`${testIdPrefix}-current`}
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 pr-10 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
          />
          <button type="button" onClick={() => setShowCur(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300" tabIndex={-1} data-testid={`${testIdPrefix}-toggle-current`}>
            {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="relative">
          <input
            type={showNew ? 'text' : 'password'}
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            placeholder="New password (min 6 chars)"
            required
            minLength={6}
            data-testid={`${testIdPrefix}-new`}
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 pr-10 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
          />
          <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300" tabIndex={-1} data-testid={`${testIdPrefix}-toggle-new`}>
            {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <input
          type={showNew ? 'text' : 'password'}
          value={confirmPw}
          onChange={e => setConfirmPw(e.target.value)}
          placeholder="Confirm new password"
          required
          minLength={6}
          data-testid={`${testIdPrefix}-confirm`}
          className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600"
        />
        <p className="text-slate-500 dark:text-zinc-600 text-xs">Tip: use a mix of upper / lower case, digits, and symbols.</p>
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testIdPrefix}-submit`}
          className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Update Password
        </button>
      </form>
    </div>
  );
}
